import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ActivatedRoute, Router } from '@angular/router';
import { sweetAlert } from 'src/app/utils/swal';
import { RouteStandingOrderService } from '../service/route-standing-orders.service';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { DriversService } from '../../../../drivers/drivers-service/drivers-service';
import { FleetService } from 'src/app/component/fleet/fleet-service/fleet.service';

@Component({
  selector: 'app-add-route-standing-orders',
  templateUrl: './add-route-standing-orders.component.html',
  styleUrls: ['./add-route-standing-orders.component.css']
})
export class AddRouteStandingOrdersComponent implements OnInit, OnDestroy {
  routeStandingOrderForm: FormGroup;
  submitted = false;
  routeStandingOrderId;
  addressPickerObj = null;
  timeZone;
  user;
  activeDayTab = 1; // Default to Monday

  days = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 }
  ];

  drivers = [];
  fleetsMap: any = {};

  constructor(
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private routeStandingOrderService: RouteStandingOrderService,
    private router: Router,
    private sharedDataService: SharedDataService,
    private driverService: DriversService,
    private fleetService: FleetService
  ) { }

  ngOnInit() {
    this.routeStandingOrderForm = this.fb.group({
      routeName: ['', Validators.required],
      routeCode: ['', Validators.required],
      description: [''],
      status: ['active', Validators.required],
      preValidFrom: ['', Validators.required],
      validFrom: ['', Validators.required],
      preValidTo: ['', Validators.required],
      validTo: ['', Validators.required],
      daySchedules: this.fb.array([]),
      stops: this.fb.array([], [this.minStopsValidator(2)])
    });

    this.getUser();

    this.activatedRoute.params
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe((params) => {
        this.routeStandingOrderId = params["id"];
        if (this.routeStandingOrderId) {
          this.getRouteStandingOrderById();
        } else {
          this.initializeEmptySchedules();
        }
      });
    // this.loadSampleData();
  }

  minStopsValidator(min: number) {
    return (control: any) => {
      return control && control.length >= min
        ? null
        : { minStops: true };
    };
  }

  getUser() {
    this.sharedDataService
      .getUser()
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(user => {
        if (user && user.timeZone) {
          this.user = user;
          this.timeZone = user.timeZone;
          this.getDriverList();

          if (!this.routeStandingOrderId) {
            const today = moment().tz(this.timeZone);
            const from = today.clone().startOf('day');
            const to = today.clone().endOf('day');
            this.routeStandingOrderForm.patchValue({
              preValidFrom: from,
              validFrom: from.toISOString(),
              preValidTo: to,
              validTo: to.toISOString()
            });
          }
        }
      });
  }

  getDriverList() {
    this.driverService
      .getDrivers(this.user._id, 'active')
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(data => {
        const drivers = Array.isArray(data) ? data : (data && data.data ? data.data : []);
        if (drivers.length) {
          this.fleetService.getFleetList({ company: this.user._id })
            .pipe(takeUntil(componentDestroyed(this)))
            .subscribe(fRes => {
              const fleets = Array.isArray(fRes) ? fRes : (fRes && fRes.data ? fRes.data : []);
              this.fleetsMap = {};
              fleets.forEach(f => this.fleetsMap[f._id] = f);

              this.drivers = drivers.map(d => {
                const fleetId = (d.fleet && typeof d.fleet === 'object') ? d.fleet._id : d.fleet;
                const fleetInfo = this.fleetsMap[fleetId] || (typeof d.fleet === 'object' ? d.fleet : null);
                return {
                  ...d,
                  vehicleInfo: fleetInfo
                };
              }).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
            });
        }
      }, err => {
        sweetAlert('Error', err, 'warning', 'OK');
      });
  }

  initializeEmptySchedules() {
    const daySchedules = this.routeStandingOrderForm.get('daySchedules') as FormArray;
    this.days.forEach(day => {
      daySchedules.push(this.fb.group({
        dayOfWeek: [day.value],
        schedules: this.fb.array([])
      }));
    });
  }

  get stops() {
    return this.routeStandingOrderForm.get('stops') as FormArray;
  }

  get daySchedules() {
    return this.routeStandingOrderForm.get('daySchedules') as FormArray;
  }

  get hasAtLeastOneSchedule() {
    if (!this.daySchedules || !this.daySchedules.controls) return false;
    return this.daySchedules.controls.some((day: any) => {
      const schedules = day.get('schedules') as FormArray;
      return schedules && schedules.length > 0;
    });
  }

  get totalSchedules() {
    if (!this.daySchedules || !this.daySchedules.controls) return 0;
    return this.daySchedules.controls.reduce((total: number, day: any) => {
      const schedules = day.get('schedules') as FormArray;
      return total + (schedules ? schedules.length : 0);
    }, 0);
  }

  addStop() {
    this.stops.push(this.fb.group({
      name: ['', Validators.required],
      address: ['', Validators.required],
      latitude: [null, Validators.required],
      longitude: [null, Validators.required],
      waitTime: [5, Validators.required]
    }));
  }

  removeStop(index: number) {
    this.stops.removeAt(index);
  }

  addSchedule(dayIndex: number) {
    const schedules = this.daySchedules.at(dayIndex).get('schedules') as FormArray;
    const start = moment.tz(this.timeZone);
    const scheduleGroup = this.fb.group({
      preStartTime: [start, Validators.required],
      startTime: [start.format('HH:mm'), Validators.required],
      driver: [null],
      fleet: [null]
    });
    schedules.push(scheduleGroup);
  }

  removeSchedule(dayIndex: number, scheduleIndex: number) {
    const schedules = this.daySchedules.at(dayIndex).get('schedules') as FormArray;
    schedules.removeAt(scheduleIndex);
  }

  getSchedulesControls(dayIndex: number) {
    if (this.daySchedules && this.daySchedules.length > dayIndex) {
      const day = this.daySchedules.at(dayIndex);
      if (day) {
        return (day.get('schedules') as FormArray).controls;
      }
    }
    return [];
  }


  loadAddressPicker(title, value, address?, lat?, lng?, zipCode?) {
    this.addressPickerObj = { title, value, address, lat, lng, zipCode };
  }

  onAddressPicker(event) {
    this.addressPickerObj = null;
    if (!event) return;

    const stopIndex = parseInt(event.reference.value);
    if (!isNaN(stopIndex)) {
      const stopGroup = this.stops.at(stopIndex) as FormGroup;
      stopGroup.patchValue({
        address: event.address,
        latitude: event.lat,
        longitude: event.lng
      });
    }
  }

  setStopAddress(place, index: number) {
    if (place && place.geometry) {
      const stopGroup = this.stops.at(index) as FormGroup;
      stopGroup.patchValue({
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng()
      });
    }
  }

  setScheduleTimeChange(event, dayIndex: number, scheduleIndex: number) {
    if (event) {
      const schedules = this.daySchedules.at(dayIndex).get('schedules') as FormArray;
      const scheduleGroup = schedules.at(scheduleIndex) as FormGroup;
      const time = moment.tz(event, this.timeZone).format("HH:mm");
      scheduleGroup.patchValue({
        startTime: time
      });

    }
  }

  onDriverChange(driver, dayIndex: number, sIndex: number) {
    const schedules = this.daySchedules.at(dayIndex).get('schedules') as FormArray;
    const scheduleGroup = schedules.at(sIndex) as FormGroup;
    if (driver) {
      const fleetId = (driver.fleet && typeof driver.fleet === 'object') ? driver.fleet._id : driver.fleet;
      scheduleGroup.patchValue({
        fleet: fleetId
      });
    } else {
      scheduleGroup.patchValue({
        fleet: null
      });
    }
  }


  getRouteStandingOrderById() {
    this.routeStandingOrderService.getRouteStandingOrdersbyId(this.routeStandingOrderId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.patchForm(data);
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        }
      );
  }

  patchForm(data) {
    while (this.stops.length) this.stops.removeAt(0);
    while (this.daySchedules.length) this.daySchedules.removeAt(0);

    if (data.stops) {
      data.stops.forEach(stop => {
        this.stops.push(this.fb.group(stop));
      });
    }

    // Patch daySchedules
    this.days.forEach(day => {
      const dayData = data.daySchedules ? data.daySchedules.find(ds => ds.dayOfWeek === day.value) : null;
      const schedulesArray = this.fb.array([]);
      if (dayData && dayData.schedules) {
        dayData.schedules.forEach(s => {
          const preDate = s.startTime ? moment.tz(s.startTime, "HH:mm", this.timeZone) : moment.tz(this.timeZone);
          schedulesArray.push(this.fb.group({
            preStartTime: [preDate],
            startTime: [s.startTime, Validators.required],
            driver: [s.driver || null],
            fleet: [s.fleet || null]
          }));
        });
      }
      this.daySchedules.push(this.fb.group({
        dayOfWeek: [day.value],
        schedules: schedulesArray
      }));
    });

    this.routeStandingOrderForm.patchValue({
      routeName: data.name,
      routeCode: data.code,
      description: data.description,
      status: data.status,
      preValidFrom: data.validFrom ? moment.tz(data.validFrom, this.timeZone) : '',
      validFrom: data.validFrom || '',
      preValidTo: data.validTo ? moment.tz(data.validTo, this.timeZone) : '',
      validTo: data.validTo || ''
    });
  }


  saveRouteStandingOrder() {
    this.submitted = true;
    if (this.routeStandingOrderForm.invalid) {
      return;
    }

    if (this.stops.length === 0) {
      return;
    }

    const hasAtLeastOneSchedule = this.daySchedules.controls.some((day: any) => {
      const schedules = day.get('schedules') as FormArray;
      return schedules.length > 0;
    });

    if (!hasAtLeastOneSchedule) {
      return;
    }

    let payload = this.routeStandingOrderForm.value;
    // Map internal form names back to API names if needed
    const apiPayload = {
      name: payload.routeName,
      code: payload.routeCode,
      description: payload.description,
      status: payload.status,
      validFrom: payload.validFrom,
      validTo: payload.validTo,
      stops: payload.stops,
      daySchedules: payload.daySchedules
    };

    this.routeStandingOrderService.saveRouteStandingOrders(this.routeStandingOrderId, apiPayload)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (data) => {
          if (data) {
            this.router.navigateByUrl("transit/standing-orders");
          }
        },
        (err) => {
          sweetAlert("Error", err, "error", "Ok");
        }
      );
  }

  get form() {
    return this.routeStandingOrderForm.controls;
  }

  setValidToTimeChange(evt): void {
    this.routeStandingOrderForm.patchValue({
      validTo: moment.tz(evt, this.timeZone).endOf('day').toISOString(),
    });
  }

  setValidFromTimeChange(evt): void {
    this.routeStandingOrderForm.patchValue({
      validFrom: moment.tz(evt, this.timeZone).startOf('day').toISOString(),
    });
  }
  getRigNumber(item: any): string {
    if (!item) return '';
    let fleetId = item.fleet;
    if (item.fleet && typeof item.fleet === 'object') {
      fleetId = item.fleet._id;
    }
    const fleet = item.vehicleInfo || this.fleetsMap[fleetId];
    return (fleet && fleet.setiDecall) ? fleet.setiDecall : '';
  }

  ngOnDestroy(): void {
  }
}
